<script>
/* === Imports === */
import MetisMenu from "metismenujs/dist/metismenujs";
import { getAccess } from "./menu";
import { erpUserRolesMethods, erpUserRolesComputed } from "@/state/helpers";
import AgentMenu from "@/views/pages/agent/AgentMenu";
import SkeletonLoader from "./SkeletonLoader.vue";
import PeddlrOptions from "../views/pages/maintenance/PeddlrOptionsModal.vue";

/* === Helpers (pure) === */
const slug = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/** Attach stable IDs without requiring manual `id` / `parentId` in menu data */
function withStableIds(items, parentPath = "", seen = new Set()) {
  if (!Array.isArray(items)) return [];
  const join = (a, b) => (a ? `${a}/${b}` : b);

  return items.map((node, i) => {
    const n = { ...node };

    // Prefer route name (most stable), else label slug
    const baseKey = n.link?.name ? `route-${n.link.name}` : slug(n.label ?? `item-${i}`);
    const path = join(parentPath, baseKey);
    const prefix = n.isTitle ? "title" : "item";

    // Keep existing ID if present; else generate a stable one
    let id = n.id || `${prefix}:${path}`;
    let k = id, c = 2;
    while (seen.has(k)) k = `${id}~${c++}`;
    n.id = k;
    seen.add(k);

    if (Array.isArray(n.subItems) && n.subItems.length) {
      n.subItems = n.subItems.map((ch) => ({ ...ch, parentId: n.id }));
      n.subItems = withStableIds(n.subItems, path, seen);
    }
    return n;
  });
}

export default {
  name: "Sidebar",

  components: {
    AgentMenu,
    SkeletonLoader,
    PeddlrOptions,
  },

  data() {
    return {
      // data
      menuItems: [],
      sectionCollapsedStates: {},

      // UI state
      modals: {
        agentModal: false,
        peddlrOptionsModal: false,
      },
      menuReady: false,

      // external libs
      menuRef: null,
    };
  },

  computed: {
    ...erpUserRolesComputed,
    routeName() {
      return this.$route?.name || "";
    },
  },

  watch: {
    $route() {
      const hash = location.hash;
      if (hash === "#agent-menu") this.modals.agentModal = true;
      if (hash === "#peddlr-options") this.modals.peddlrOptionsModal = true;
      
      // Update active menu item when route changes
      this.$nextTick(() => {
        this.markActiveFromLocation();
      });
    },
    menuItems: {
      deep: true,
      handler() {
        this.$nextTick(() => this.initMetis());
      },
    },
  },

  async mounted() {
    const [raw] = await Promise.all([getAccess()]);
    const pruned = this.pruneDisabledTree(raw || []);
    this.menuItems = withStableIds(pruned);

    this.menuItems.forEach((it) => {
      if (it.isTitle && it.isCollapsible) this.$set(this.sectionCollapsedStates, it.id, true);
    });

    await this.$nextTick();
    this.initMetis();
    this.markActiveFromLocation();
    this.menuReady = true;

    const hash = location.hash;
    if (hash === "#agent-menu") this.modals.agentModal = true;
    if (hash === "#peddlr-options") this.modals.peddlrOptionsModal = true;
  },

  beforeDestroy() {
    if (this.menuRef?.dispose) this.menuRef.dispose();
    this.menuRef = null;
  },

  methods: {
    ...erpUserRolesMethods,

    hasItems(item) {
      return Array.isArray(item?.subItems) && item.subItems.length > 0;
    },

    pruneDisabledTree(list) {
      if (!Array.isArray(list)) return [];

      const prune = (node) => {
        if (!node) return null;

        // 0) HARD GATE: explicit false removes the whole subtree (title + items)
        if (node.enabled === false) return null;

        // 1) prune children
        const kids = Array.isArray(node.subItems)
          ? node.subItems.map(prune).filter(Boolean)
          : [];

        // 2) treat undefined as “no gate” (allowed); anything truthy keeps the node
        const selfEnabled = node.enabled === undefined ? true : !!node.enabled;

        // 3) if node is falsy-enabled (e.g. 0/""/null) and has no children -> drop
        if (!selfEnabled && kids.length === 0) return null;

        // 4) drop empty non-action groups
        const isTitle   = !!node.isTitle;
        const hasAction = !!(node.link || node.modal);
        if (!isTitle && !hasAction && kids.length === 0) return null;

        return { ...node, subItems: kids };
      };

      return list.map(prune).filter(Boolean);
    },

    /* ---------- DOM / Metis ---------- */
    initMetis() {
      const el = document.querySelector("#side-menu");
      if (!el) return;

      // Prevent Metis from binding inside section-wrapper content
      el.querySelectorAll(".section-wrapper .sub-menu").forEach((ul) => {
        ul.classList.add("mm-collapse");
        ul.setAttribute("data-disable-metis", "true");
      });

      try {
        if (this.menuRef?.dispose) this.menuRef.dispose();
        this.menuRef = new MetisMenu(el, {
          toggle: true,
          onTransitionStart: (node) => {
            if (node.parentElement.closest(".section-wrapper")) return false;
          },
        });
      } catch (e) {
        console.warn("Metis init ignored for section wrappers", e);
      }
    },

    markActiveFromLocation() {
      const sideMenu = document.getElementById("side-menu");
      if (!sideMenu) return;

      sideMenu.querySelectorAll(".mm-active").forEach((el) => el.classList.remove("mm-active"));
      sideMenu.querySelectorAll(".mm-show").forEach((el) => el.classList.remove("mm-show"));
      sideMenu.querySelectorAll("a.side-nav-link-ref.active").forEach((el) => el.classList.remove("active"));

      const links = Array.from(sideMenu.getElementsByClassName("side-nav-link-ref"));
      if (!links.length) return;

      const currentPath = (window.location.pathname || "/").replace(/\/+$/, "") || "/";
      
      let match = links.find((a) => (a.pathname || "").replace(/\/+$/, "") === currentPath);
      
      if (!match) {
        const matchingLinks = links.filter((a) => {
          const linkPath = (a.pathname || "").replace(/\/+$/, "");
          return linkPath && currentPath.startsWith(linkPath);
        });
        
        if (matchingLinks.length > 0) {
          matchingLinks.sort((a, b) => {
            const pathA = (a.pathname || "").replace(/\/+$/, "");
            const pathB = (b.pathname || "").replace(/\/+$/, "");
            return pathB.length - pathA.length;
          });
          match = matchingLinks[0];
        }
      }

      if (!match) return;
      match.classList.add("active");

      let li = match.closest("li");
      if (!li) return;
      li.classList.add("mm-active");

      let ul = li.parentElement;
      while (ul && ul !== sideMenu) {
        ul.classList.add("mm-show");

        const ownerLi = ul.closest("li");
        if (!ownerLi) break;

        if (!ownerLi.classList.contains("section-wrapper")) {
          ownerLi.classList.add("mm-active");
          const ownerToggle = ownerLi.querySelector(
            ":scope > a.has-arrow, :scope > a.has-dropdown, :scope > a.is-parent"
          );
          if (ownerToggle) ownerToggle.classList.add("mm-active");
        }

        ul = ownerLi.parentElement;
      }

      this._syncSectionStateToMatch(match);
    },

    _syncSectionStateToMatch(matchEl) {
      const sideMenu = document.getElementById("side-menu");
      if (!sideMenu) return;

      sideMenu.querySelectorAll(".section-wrapper").forEach((w) => {
        const ul = w.querySelector("ul.collapsible-section-content");
        const id = ul?.id?.replace(/^sec-/, "");
        if (id) this.$set(this.sectionCollapsedStates, id, true);
      });

      const wrapper = matchEl?.closest(".section-wrapper");
      if (wrapper) {
        const ul = wrapper.querySelector("ul.collapsible-section-content");
        const id = ul?.id?.replace(/^sec-/, "");
        if (id) this.$set(this.sectionCollapsedStates, id, false);
      }

      this.$forceUpdate();
    },

    handleSidebarSize(size) {
      const sideMenu = document.getElementById("side-menu");
      if (!sideMenu) return;

      if (size === "icon" || document.body.getAttribute("data-sidebar-size") === "sm") {
        sideMenu.querySelectorAll(".mm-show").forEach((el) => el.classList.remove("mm-show"));
        sideMenu.querySelectorAll("li.mm-active").forEach((li) => {
          const hasActiveLink =
            !!li.querySelector(":scope > a.side-nav-link-ref.active") ||
            !!li.querySelector(":scope > .side-nav-link-ref.active");
          if (!hasActiveLink) li.classList.remove("mm-active");
        });

        Object.keys(this.sectionCollapsedStates).forEach((id) => {
          this.$set(this.sectionCollapsedStates, id, true);
        });
        this.$forceUpdate();

        if (this.menuRef?.dispose) {
          try {
            this.menuRef.dispose();
          } catch (e) {
            console.warn("side-nav: Metis dispose failed (ignored)", e);
          }
          this.menuRef = null;
        }
      } else {
        this.$nextTick(() => {
          this.initMetis();
          this.markActiveFromLocation();
        });
      }
    },

    toggleMenu(event) {
      event.currentTarget.nextElementSibling.classList.toggle("mm-show");
    },

    clearFilters() {
      localStorage.removeItem("merchantFilter");
    },

    toggleSectionCollapse(id) {
      if (!id) return;
      this.$set(this.sectionCollapsedStates, id, !this.sectionCollapsedStates[id]);
      this.$forceUpdate();
    },

    isSectionCollapsed(sectionId) {
      return !!this.sectionCollapsedStates[sectionId];
    },

    tSafe(label) {
      if (typeof label === "string") {
        try {
          if (this.$te && this.$te(label)) {
            const res = this.$t(label);
            return typeof res === "string" ? res : label;
          }
          return label;
        } catch (e) {
          return String(label);
        }
      }
      if (label && typeof label === "object") {
        const key = typeof label.key === "string" ? label.key : "";
        if (key && this.$te && this.$te(key)) {
          const res = this.$t(key);
          return typeof res === "string" ? res : String(label.text ?? key);
        }
        return String(label.text ?? key ?? "");
      }
      return String(label ?? "");
    },

    isCustomIconSvg(customIcon) {
      if (!customIcon) return false;
      let iconContent = "";
      if (typeof customIcon === "object" && customIcon.icon) iconContent = customIcon.icon;
      else if (typeof customIcon === "string") iconContent = customIcon;
      else return false;

      if (iconContent.trim().toLowerCase().startsWith("<svg")) return true;
      if (iconContent.startsWith("data:image/svg+xml")) return true;
      return false;
    },

    getIconContent(customIcon) {
      if (!customIcon) return "";
      if (typeof customIcon === "string") return customIcon;
      if (typeof customIcon === "object" && customIcon.icon) return customIcon.icon;
      return "";
    },

    getIconCssProperties(customIcon) {
      if (!customIcon || typeof customIcon !== "object") return "";
      return customIcon.css_properties || "";
    },

    parseCssProperties(cssString) {
      if (!cssString || typeof cssString !== "string") return {};
      const styles = {};
      const declarations = cssString.split(";").filter((d) => d.trim());
      declarations.forEach((decl) => {
        const [property, value] = decl.split(":").map((s) => s.trim());
        if (property && value) {
          const camel = property.replace(/-([a-z])/g, (_, l) => l.toUpperCase());
          styles[camel] = value;
        }
      });
      return styles;
    },
  },
};
</script>

<template>
  <div id="sidebar-menu">
    <skeleton-loader v-if="menuItems.length === 0" />

    <ul
      v-else
      id="side-menu"
      class="metismenu list-unstyled"
      :class="{ 'pre-hydrate': !menuReady }"
    >
      <template v-for="item in menuItems">
        <!-- ===== Title row ===== -->
        <li
          v-if="item.isTitle"
          :key="'title-' + (item.id || item.label)"
          class="menu-title title-as-item"
        >
          <div class="menu-title-wrapper">
            <span class="">{{ tSafe(item.label) }}</span>

            <button
              v-if="item.isCollapsible"
              class="section-collapse-btn"
              type="button"
              :title="`${isSectionCollapsed(item.id) ? 'Expand' : 'Collapse'} ${tSafe(item.label)}`"
              :aria-expanded="!isSectionCollapsed(item.id)"
              :aria-controls="`sec-${item.id}`"
              :class="{ 'collapsed': isSectionCollapsed(item.id) }"
              @click.stop.prevent="toggleSectionCollapse(item.id)"
            >
              <i :class="isSectionCollapsed(item.id) ? 'mdi mdi-plus' : 'mdi mdi-minus'"></i>
            </button>
          </div>
        </li>

        <!-- ===== Collapsible Title Content ===== -->
        <li
          v-if="item.isTitle && item.isCollapsible"
          :key="'title-content-' + (item.id || item.label)"
          class="section-wrapper"
        >
          <ul
            :id="`sec-${item.id}`"
            class="collapsible-section-content list-unstyled"
            :class="isSectionCollapsed(item.id) ? 'mm-collapse' : 'mm-show'"
            :aria-expanded="!isSectionCollapsed(item.id)"
            role="group"
          >
            <li
              v-for="subitem in item.subItems"
              :key="'subitem-' + (subitem.id || subitem.label)"
              :class="subitem.link && subitem.link.name === routeName ? 'mm-active' : ''"
            >
              <!-- Parent with children -->
              
              <a
                v-if="hasItems(subitem)"
                href="javascript:void(0);"
                class="is-parent"
                :class="{ 'has-arrow': !subitem.badge, 'has-dropdown': subitem.badge }"
              >
                <box-icon v-if="subitem.icon" :name="subitem.icon" class="icon-xl nav-icon bx-fw me-2" />
                <img
                  v-else-if="subitem.customIcon && !isCustomIconSvg(subitem.customIcon)"
                  :src="getIconContent(subitem.customIcon)"
                  class="nav-icon"
                  :style="parseCssProperties(getIconCssProperties(subitem.customIcon))"
                />
                <div
                  v-else-if="subitem.customIcon && isCustomIconSvg(subitem.customIcon)"
                  class="nav-icon custom-svg-icon"
                  :style="parseCssProperties(getIconCssProperties(subitem.customIcon))"
                  v-html="getIconContent(subitem.customIcon)"
                ></div>
                <span class="label-text">{{ tSafe(subitem.label) }}</span>
                <span
                  v-if="subitem.badge"
                  :class="`badge rounded-pill bg-${subitem.badge.variant} float-end`"
                >
                  {{ tSafe(subitem.badge.text) }}
                </span>
                
              </a>
             
              <!-- Plain link (fixed: use subitem.*) -->
              <router-link
                v-else-if="subitem.link"
                class="side-nav-link-ref"
                :to="subitem.link"
                @click.native="clearFilters()"
              >
                <box-icon v-if="subitem.icon" :name="subitem.icon" class="icon-xl nav-icon bx-fw me-2" />
                <img
                  v-else-if="subitem.customIcon && !isCustomIconSvg(subitem.customIcon)"
                  :src="getIconContent(subitem.customIcon)"
                  class="nav-icon"
                  :style="parseCssProperties(getIconCssProperties(subitem.customIcon))"
                />
                <div
                  v-else-if="subitem.customIcon && isCustomIconSvg(subitem.customIcon)"
                  class="nav-icon custom-svg-icon"
                  :style="parseCssProperties(getIconCssProperties(subitem.customIcon))"
                  v-html="getIconContent(subitem.customIcon)"
                ></div>
                <span class="label-text">{{ tSafe(subitem.label) }}</span>
                <span
                  v-if="subitem.badge"
                  :class="`badge rounded-pill ${subitem.badge.variant} float-end`"
                >
                  {{ tSafe(subitem.badge.text) }}
                </span>
              </router-link>

              <!-- Modal trigger -->
              <a
                v-else-if="subitem.modal"
                class="clickable"
                @click="modals[subitem.modal.key] = true"
              >
                <box-icon v-if="subitem.icon" :name="subitem.icon" class="icon-xl nav-icon bx-fw me-2" />
                <img
                  v-else-if="subitem.customIcon && !isCustomIconSvg(subitem.customIcon)"
                  :src="getIconContent(subitem.customIcon)"
                  class="nav-icon"
                  :style="parseCssProperties(getIconCssProperties(subitem.customIcon))"
                />
                <div
                  v-else-if="subitem.customIcon && isCustomIconSvg(subitem.customIcon)"
                  class="nav-icon custom-svg-icon"
                  :style="parseCssProperties(getIconCssProperties(subitem.customIcon))"
                  v-html="getIconContent(subitem.customIcon)"
                ></div>
                <span class="label-text">{{ tSafe(subitem.label) }}</span>
                <span v-if="subitem.badge" :class="`badge rounded-pill ${subitem.badge.variant} float-end`">
                  {{ tSafe(subitem.badge.text) }}
                </span>
              </a>

              <!-- Children -->
              <ul v-if="hasItems(subitem)" class="sub-menu" aria-expanded="false">
                <li
                  v-for="subsubitem in subitem.subItems"
                  :key="'subsubitem-' + (subsubitem.id || subsubitem.label)"
                  :class="subsubitem.link && subsubitem.link.name === routeName ? 'mm-active' : ''"
                >
                  <router-link
                    v-if="subsubitem.link"
                    class="side-nav-link-ref"
                    :to="subsubitem.link"
                    @click.native="clearFilters()"
                  >
                    <span class="label-text">{{ tSafe(subsubitem.label) }}</span>
                  </router-link>

                  <!-- Modal trigger for nested items -->
                  <a
                    v-else-if="subsubitem.modal"
                    class="clickable"
                    @click="modals[subsubitem.modal.key] = true"
                  >
                    <box-icon v-if="subsubitem.icon" :name="subsubitem.icon" class="icon-xl nav-icon bx-fw me-2" />
                    <span class="label-text">{{ tSafe(subsubitem.label) }}</span>
                    <span v-if="subsubitem.badge" :class="`badge rounded-pill ${subsubitem.badge.variant} float-end`">
                      {{ tSafe(subsubitem.badge.text) }}
                    </span>
                  </a>
                </li>
              </ul>

              <hr v-if="subitem.hasSeparator" class="my-2 mx-4" />
            </li>
          </ul>
          <hr v-if="item.hasSeparator" class="my-2 mx-4" />
        </li>

        <!-- ===== Regular (non-title) items ===== -->
        <li
          v-else
          :key="'item-' + (item.id || item.label)"
          :class="item.link && item.link.name === routeName ? 'mm-active' : ''"
        >
          <!-- Parent with children -->
          <a
            v-if="hasItems(item)"
            href="javascript:void(0);"
            class="is-parent"
            :class="{ 'has-arrow': !item.badge, 'has-dropdown': item.badge }"
          >
            <box-icon v-if="item.icon" :name="item.icon" class="icon-xl nav-icon bx-fw me-2" />
            <img
              v-else-if="item.customIcon && !isCustomIconSvg(item.customIcon)"
              :src="getIconContent(item.customIcon)"
              class="nav-icon"
              :style="parseCssProperties(getIconCssProperties(item.customIcon))"
            />
            <div
              v-else-if="item.customIcon && isCustomIconSvg(item.customIcon)"
              class="nav-icon custom-svg-icon"
              :style="parseCssProperties(getIconCssProperties(item.customIcon))"
              v-html="getIconContent(item.customIcon)"
            ></div>
            <span class="label-text">{{ tSafe(item.label) }}</span>
            <span v-if="item.badge" :class="`badge rounded-pill bg-${item.badge.variant} float-end`">
              {{ tSafe(item.badge.text) }}
            </span>
          </a>

          <!-- Plain link -->
          <router-link
            v-else-if="item.link"
            class="side-nav-link-ref"
            :to="item.link"
            @click.native="clearFilters()
            "
          >
            <box-icon v-if="item.icon" :name="item.icon" class="icon-xl nav-icon bx-fw me-2" />
            <img
                  v-else-if="item.customIcon && !isCustomIconSvg(item.customIcon)"
                  :src="getIconContent(item.customIcon)"
                  class="nav-icon"
                  :style="parseCssProperties(getIconCssProperties(item.customIcon))"
                />
                <div
                  v-else-if="item.customIcon && isCustomIconSvg(item.customIcon)"
                  class="nav-icon custom-svg-icon"
                  :style="parseCssProperties(getIconCssProperties(item.customIcon))"
                  v-html="getIconContent(item.customIcon)"
                ></div>
            <span class="">{{ tSafe(item.label) }}</span>
            <span v-if="item.badge" :class="`badge rounded-pill ${item.badge.variant} float-end`">
              {{ tSafe(item.badge.text) }}
            </span>
          </router-link>

          <!-- Modal trigger -->
          <a v-else-if="item.modal" class="clickable" @click="modals[item.modal.key] = true">
            <box-icon v-if="item.icon" :name="item.icon" class="icon-xl nav-icon bx-fw me-2" />
            <img
              v-else-if="item.customIcon && !isCustomIconSvg(item.customIcon)"
              :src="getIconContent(item.customIcon)"
              class="nav-icon"
              :style="parseCssProperties(getIconCssProperties(item.customIcon))"
            />
            <div
              v-else-if="item.customIcon && isCustomIconSvg(item.customIcon)"
              class="nav-icon custom-svg-icon"
              :style="parseCssProperties(getIconCssProperties(item.customIcon))"
              v-html="getIconContent(item.customIcon)"
            ></div>
            <span class="">{{ tSafe(item.label) }}</span>
            <span v-if="item.badge" :class="`badge rounded-pill ${item.badge.variant} float-end`">
              {{ tSafe(item.badge.text) }}
            </span>
          </a>

          <!-- Children -->
          <ul v-if="hasItems(item)" class="sub-menu" aria-expanded="false">
            <li
              v-for="subitem in item.subItems"
              :key="'subitem-' + (subitem.id || subitem.label)"
              :class="subitem.link && subitem.link.name === routeName ? 'mm-active' : ''"
            >
              <!-- subgroup parent (e.g., Product Directory, Roulette) -->
              <a
                v-if="hasItems(subitem)"
                href="javascript:void(0);"
                class="is-parent"
                :class="{ 'has-arrow': !subitem.badge, 'has-dropdown': subitem.badge }"
              >
                <box-icon v-if="subitem.icon" :name="subitem.icon" class="icon-xl nav-icon bx-fw me-2" />
                <img
                  v-else-if="subitem.customIcon && !isCustomIconSvg(subitem.customIcon)"
                  :src="getIconContent(subitem.customIcon)"
                  class="nav-icon"
                  :style="parseCssProperties(getIconCssProperties(subitem.customIcon))"
                />
                <div
                  v-else-if="subitem.customIcon && isCustomIconSvg(subitem.customIcon)"
                  class="nav-icon custom-svg-icon"
                  :style="parseCssProperties(getIconCssProperties(subitem.customIcon))"
                  v-html="getIconContent(subitem.customIcon)"
                ></div>
                <span class="label-text">{{ tSafe(subitem.label) }}</span>
                <span v-if="subitem.badge" :class="`badge rounded-pill bg-${subitem.badge.variant} float-end`">
                  {{ tSafe(subitem.badge.text) }}
                </span>
              </a>

              <!-- plain link child (e.g., Merchant List) -->
              <router-link
                v-else-if="subitem.link"
                class="side-nav-link-ref"
                :to="subitem.link"
                @click.native="clearFilters()"
              >
                <box-icon v-if="subitem.icon" :name="subitem.icon" class="icon-xl nav-icon bx-fw me-2" />
                <img
                  v-else-if="subitem.customIcon && !isCustomIconSvg(subitem.customIcon)"
                  :src="getIconContent(subitem.customIcon)"
                  class="nav-icon"
                  :style="parseCssProperties(getIconCssProperties(subitem.customIcon))"
                />
                <div
                  v-else-if="subitem.customIcon && isCustomIconSvg(subitem.customIcon)"
                  class="nav-icon custom-svg-icon"
                  :style="parseCssProperties(getIconCssProperties(subitem.customIcon))"
                  v-html="getIconContent(subitem.customIcon)"
                ></div>
                <span class="label-text">{{ tSafe(subitem.label) }}</span>
                <span v-if="subitem.badge" :class="`badge rounded-pill ${subitem.badge.variant} float-end`">
                  {{ tSafe(subitem.badge.text) }}
                </span>
              </router-link>

              <!-- modal child -->
              <a v-else-if="subitem.modal" class="clickable" @click="modals[subitem.modal.key] = true">
                <box-icon v-if="subitem.icon" :name="subitem.icon" class="icon-xl nav-icon bx-fw me-2" />
                <span class="label-text">{{ tSafe(subitem.label) }}</span>
              </a>

              <!-- nested sub-submenu for subgroup -->
              <ul v-if="hasItems(subitem)" class="sub-menu" aria-expanded="false">
                <li
                  v-for="subsubitem in subitem.subItems"
                  :key="'subsubitem-' + (subsubitem.id || subsubitem.label)"
                  :class="subsubitem.link && subsubitem.link.name === routeName ? 'mm-active' : ''"
                >
                  <router-link
                    v-if="subsubitem.link"
                    class="side-nav-link-ref"
                    :to="subsubitem.link"
                    @click.native="clearFilters()"
                  >
                    <span class="label-text">{{ tSafe(subsubitem.label) }}</span>
                  </router-link>
                </li>
              </ul>

              <hr v-if="subitem.hasSeparator" class="my-2 mx-4" />
            </li>
          </ul>

          <hr v-if="item.hasSeparator" class="my-2 mx-4" />
        </li>
      </template>
    </ul>

    <agent-menu v-model="modals.agentModal" />
    <peddlr-options v-model="modals.peddlrOptionsModal" />
  </div>
</template>

<style scoped>
/* Click areas */
.clickable { cursor: pointer; }

/* Title row */
.menu-title-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px !important;
  font-size: 11px;
  letter-spacing: .08em;
  text-transform: uppercase;
  opacity: 1 !important;
  pointer-events: none;
}
.menu-title-wrapper > * { pointer-events: all; }

/* Collapse button */
.section-collapse-btn {
  background: #7F4697;
  border: none;
  border-radius: 4px;
  width: 20px; height: 20px;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 12px;
  margin-left: 8px;
  cursor: pointer; user-select: none;
  transition: transform .3s ease;
  outline: none;
  z-index: 1000;
}
.section-collapse-btn:hover { transform: scale(1.1); box-shadow: 0 2px 8px rgba(111,66,193,.4); }
.section-collapse-btn:focus { box-shadow: 0 0 0 3px rgba(111,66,193,.3); }
.section-collapse-btn:active { transform: scale(.95); }
.section-collapse-btn.collapsed,
.section-collapse-btn:active,
.section-collapse-btn:hover { background: #7F4697; }
.section-collapse-btn i { line-height: 1; font-size: 14px; transition: transform .3s ease; }

/* Pre-hydrate: hide to prevent flicker */
.pre-hydrate { visibility: hidden; }
.pre-hydrate, .pre-hydrate * { transition: none !important; animation: none !important; }

/* Collapsible content */
.collapsible-section-content {
  margin: 0; padding: 0; list-style: none;
  animation: slideDown .3s ease-out;
}
.collapsible-section-content.mm-show { display: block !important; }
.collapsible-section-content li { margin: 0; }
.collapsible-section-content li a {
  padding: 8px 16px 8px 24px;
  margin: 2px 8px;
  border-radius: 4px;
  color: #6b7280;
  opacity: 1 !important;
}
.collapsible-section-content li a span { opacity: 1 !important; } /* removed color: inherit !important */
.collapsible-section-content li a:hover { background-color: rgba(0,0,0,.05); }

/* Menu arrows */
.has-arrow:after, .has-dropdown:after {
  content: '\F0140';
  font-family: 'Material Design Icons';
  display: block; float: right;
  transition: transform .2s; font-size: 1rem; margin-left: 10px;
  color: #545a6d;
}
.mm-active > .has-arrow:after,
.mm-active > .has-dropdown:after { transform: rotate(-180deg); color: #556ee6; }

/* Animation */
@keyframes slideDown {
  from { opacity: 0; max-height: 0; overflow: hidden; }
  to   { opacity: 1; max-height: 500px; overflow: visible; }
}

/* Title spacing cleanups */
.menu-title:has(.menu-title-wrapper) { padding: 0 !important; }
.menu-title .menu-title-wrapper + .collapsible-section-content { margin-top: 8px; }

/* Custom SVG Icon styling */
.custom-svg-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.custom-svg-icon svg { width: 18px; height: 18px; fill: currentColor; }
.custom-svg-icon svg path { fill: currentColor; }

.label-text {
  /* font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  letter-spacing: 0.21px; */

  color: var(--neutrals-neutral-600, #313131);
  font-family: Roboto;
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  letter-spacing: 0.21px;
  /* padding: 16px 12px; */
}
box-icon {
  margin-top: 2px !important;
  width: 20px !important;
  height: 20px !important;
}
</style>
